import dayjs from 'dayjs'
import { SupplierSections } from './supplierSection'
import { formatCurrency } from 'src/components/common/helpers'
import { DocumentType, SHORT_DATE_FORMAT } from '$constants'
import { ISupplierStatement, IStockReceived } from '$models'

/**
 * Returns the sum of a specified field for an array of orders.
 *
 * @param stockReceivedItems - The array of orders to sum
 * @param field - The field to sum with
 * @returns The total sum of the specified field
 */
const calculateTotals = (
  stockReceivedItems: IStockReceived[],
  field: string
): number => {
  return stockReceivedItems.reduce(
    (total, order) => total + parseFloat(order[field] || '0'),
    0
  )
}

/**
 * Returns the count of unreconciled orders.
 *
 * @param stockReceivedItems - The array of orders to count
 * @returns The count of unreconciled orders
 */
const countUnreconciled = (stockReceivedItems: IStockReceived[]): number => {
  return stockReceivedItems.reduce(
    (count, order) => count + (!order.reconciled ? 1 : 0),
    0
  )
}

/**
 * Fetches and processes supplier sections by grouping orders and statements
 * and calculating totals, freight, and unreconciled counts.
 *
 * @param stockReceivedItems - The array of all orders
 * @param statements - The array of all statements
 * @returns The grouped supplier sections or null
 */
export const buildSupplierSections = (
  stockReceivedItems: IStockReceived[] = [],
  statements: ISupplierStatement[] = []
): SupplierSections | null => {
  const supplierSections: SupplierSections = {}

  // Group orders by supplierId
  addStockReceivedItemsToSupplierSections(supplierSections, stockReceivedItems)

  // Group statements by supplierId
  addStatementsToSupplierSections(supplierSections, statements)

  // Calculate totals, freight, and unreconciled orders for each supplier
  calculateSectionTotals(supplierSections)

  // Sort the supplier sections by supplier name
  const sortedSupplierSections = sortSupplierSections(supplierSections)

  // Return sorted supplier sections
  return Object.keys(sortedSupplierSections).length > 0
    ? sortedSupplierSections
    : null
}

/**
 * Add orders per supplier
 */
const addStockReceivedItemsToSupplierSections = (
  supplierSections: SupplierSections,
  stockReceivedItems: IStockReceived[]
) => {
  stockReceivedItems.forEach((stockReceived) => {
    if (!stockReceived.supplierId || !stockReceived.supplierName) return

    if (!supplierSections[stockReceived.supplierId]) {
      supplierSections[stockReceived.supplierId] = {
        supplierId: stockReceived.supplierId,
        supplierName: stockReceived.supplierName,
        stockReceivedRows: [],
        statements: [],
        subtotal: 0,
        freight: 0,
        total: 0,
        unreconciled: 0,
      }
    }

    supplierSections[stockReceived.supplierId].stockReceivedRows.push(
      stockReceived
    )
  })
}

/**
 * Add statements per supplier
 */
const addStatementsToSupplierSections = (
  supplierSections: SupplierSections,
  statements: ISupplierStatement[]
) => {
  statements.forEach((statement) => {
    if (!statement.supplierId || !statement.supplierName) return

    if (!supplierSections[statement.supplierId]) {
      supplierSections[statement.supplierId] = {
        supplierId: statement.supplierId,
        supplierName: statement.supplierName,
        stockReceivedRows: [],
        statements: [],
        subtotal: 0,
        freight: 0,
        total: 0,
        unreconciled: 0,
      }
    }

    supplierSections[statement.supplierId].statements!.push(statement)
  })
}

/**
 * Calculate totals, freight, and unreconciled orders/statements for each supplier
 */
const calculateSectionTotals = (supplierSections: SupplierSections) => {
  // Calculate totals, freight, and unreconciled orders for each supplier
  Object.keys(supplierSections).forEach((supplierId) => {
    const section = supplierSections[supplierId]

    section.subtotal = calculateTotals(section.stockReceivedRows, 'subtotal')
    section.freight = calculateTotals(section.stockReceivedRows, 'freight')
    section.total = calculateTotals(section.stockReceivedRows, 'total')
    section.unreconciled = countUnreconciled(section.stockReceivedRows)
  })
}

/**
 * Sort the supplier sections by suppliername
 */
const sortSupplierSections = (
  supplierSections: SupplierSections
): SupplierSections => {
  return Object.keys(supplierSections)
    .sort((a, b) => {
      const nameA = supplierSections[a].supplierName.toLowerCase()
      const nameB = supplierSections[b].supplierName.toLowerCase()
      return nameA.localeCompare(nameB)
    })
    .reduce((sortedSections: SupplierSections, supplierId) => {
      sortedSections[supplierId] = supplierSections[supplierId]
      return sortedSections
    }, {} as SupplierSections)
}

/**
 * Get supplier table row content values
 */
export const getSupplierTableRowContentValues = (
  stockReceived: IStockReceived,
  locationSearch: string | undefined
) => {
  // Common fields that don't change based on document type
  const formattedInvoicedAt = stockReceived.invoicedAt
    ? dayjs(stockReceived.invoicedAt).format(SHORT_DATE_FORMAT)
    : ''
  const amountExcGstValue = formatCurrency(stockReceived.subtotal)
  const freightValue = formatCurrency(stockReceived.freight)
  const amountIncGstValue = formatCurrency(stockReceived.total)

  const orderNumberValue = stockReceived.purchaseOrderNumber
    ? getPurchaseOrderNumberWithPrefix(
        stockReceived.purchaseOrderNumber,
        stockReceived.creditRequest
      )
    : undefined
  if (stockReceived.type === DocumentType.PurchaseOrder) {
    return {
      invoiceNoLinkUrl: `/stock/report/supplier/edit/${stockReceived.id}${locationSearch}`,
      invoiceNumberValue: stockReceived.invoiceNumber,
      invoicedAtValue: formattedInvoicedAt,
      orderedByValue: stockReceived.orderedBy,
      orderNumberValue: orderNumberValue,
      amountExcGstValue,
      freightValue,
      amountIncGstValue,
    }
  } else {
    return {
      invoiceNoLinkUrl:
        stockReceived.type === DocumentType.Invoice
          ? `/stock/invoices/${stockReceived.id}`
          : undefined,
      invoiceNumberValue: stockReceived.invoiceNumber
        ? stockReceived.invoiceNumber
        : '-',
      invoicedAtValue: formattedInvoicedAt,
      orderedByValue: stockReceived.orderedBy,
      orderNumberValue: orderNumberValue,
      amountExcGstValue,
      freightValue,
      amountIncGstValue,
    }
  }
}

export const getPurchaseOrderNumberWithPrefix = (
  purchaseOrderNumber: string,
  isCreditNote: boolean = false
) => {
  return (isCreditNote ? 'CR' : 'PO') + `#${purchaseOrderNumber}`
}
